import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api, ApiError } from "../api/client";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function ChangePasswordModal({ open, onClose }: Props) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const change = useMutation({
    mutationFn: () => api.localChangePassword(current, next),
    onSuccess: () => {
      setOk(true);
      setErr(null);
      setCurrent("");
      setNext("");
      setConfirm("");
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError) {
        if (e.status === 401) setErr("Current password is wrong.");
        else if (e.status === 429) setErr("Too many attempts. Wait a few minutes.");
        else setErr("Password change failed.");
      } else {
        setErr("Password change failed.");
      }
    },
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (next.length < 8) {
            setErr("New password must be at least 8 characters.");
            return;
          }
          if (next !== confirm) {
            setErr("New passwords don't match.");
            return;
          }
          change.mutate();
        }}
        className="w-full max-w-sm rounded-lg border border-zinc-800 bg-zinc-900 p-6 shadow-xl"
      >
        <h2 className="mb-4 text-lg font-semibold text-zinc-100">Change password</h2>
        <label className="mb-3 block text-xs text-zinc-400">
          Current password
          <input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            className="mt-1 block w-full rounded border border-zinc-700 bg-zinc-800 px-2 py-1.5 text-zinc-100"
          />
        </label>
        <label className="mb-3 block text-xs text-zinc-400">
          New password
          <input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            className="mt-1 block w-full rounded border border-zinc-700 bg-zinc-800 px-2 py-1.5 text-zinc-100"
          />
        </label>
        <label className="mb-4 block text-xs text-zinc-400">
          Confirm new password
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            className="mt-1 block w-full rounded border border-zinc-700 bg-zinc-800 px-2 py-1.5 text-zinc-100"
          />
        </label>
        {err && (
          <div className="mb-3 rounded bg-rose-900/40 px-2 py-1 text-xs text-rose-200">{err}</div>
        )}
        {ok && (
          <div className="mb-3 rounded bg-emerald-900/40 px-2 py-1 text-xs text-emerald-200">
            Password changed.
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded bg-zinc-800 px-3 py-1.5 text-sm text-zinc-200 hover:bg-zinc-700"
          >
            Close
          </button>
          <button
            type="submit"
            disabled={change.isPending}
            className="rounded bg-emerald-700 px-3 py-1.5 text-sm text-white hover:bg-emerald-600 disabled:opacity-50"
          >
            {change.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
